import { useCallback, useMemo } from 'react';

import usePayouts from 'api/usePayouts';
import usePermissions from 'api/usePermissions';
import isEmpty from 'lodash/isEmpty';
import { permissionsUtil } from 'utils/permissionsUtil';
import { EToasterStatus, showToast } from 'utils/showToast';

import EmptyState from 'components/EmptyState/EmptyState';
import { TableView } from 'components/TableView';

import { PAYOUTS_TABLE_COLUMNS } from '../constants/payouts';
import { createPayoutTableRow, transformPayoutsToTableData } from '../utils';

const PayoutsTable = () => {
  const { getPermissions } = usePermissions();
  const permissions = getPermissions();

  const {
    getPayouts,
    getPayoutDownloadNewVersion
  } = usePayouts(
    permissions
      ? permissionsUtil.getPublishersByPayouts(permissions).join(',')
      : undefined
  );

  const onDownloadClick = useCallback((
    payoutId: string,
    publisherId?: string | undefined,
    period?: string | undefined
  ) => {
    if (publisherId && period) {
      getPayoutDownloadNewVersion(payoutId, publisherId, period);
      showToast({
        message: 'Payout download started',
        status: EToasterStatus.SUCCESS
      });
    }
  }, [getPayoutDownloadNewVersion]);

  const columns = useMemo(() => {
    return PAYOUTS_TABLE_COLUMNS(onDownloadClick);
  }, [onDownloadClick]);

  const tableData = useMemo(() => {
    if (!getPayouts.data) return [];
    const payoutsWithIds = transformPayoutsToTableData(getPayouts.data);
    return payoutsWithIds.map(createPayoutTableRow);
  }, [getPayouts.data]);

  const isEmptyState = !getPayouts.isLoading && isEmpty(tableData);

  return (
    <>
      {isEmptyState ? (
        <div className="flex items-center justify-center w-full min-h-[calc(100vh-250px)]">
          <EmptyState
            title="No payouts yet"
            description={
              <>
                You don't have any payouts yet.
                <br />
                Payouts will appear here once they are processed.
              </>
            }
            icon="fa-regular fa-file-chart-column"
            hideButton={true}
          />
        </div>
      ) : (
        <TableView
          title="Payouts"
          data={tableData}
          columns={columns}
          isLoading={getPayouts.isLoading}
        />
      )}
    </>
  );
};

export default PayoutsTable;

